/**
 * @license
 * SPDX-License-Identifier: Apache-2.0
 */

import React, { createContext, useContext, useState, useEffect } from 'react';
import { 
  StudyMaterial, 
  ClassEntity, 
  SubjectEntity, 
  ChapterEntity, 
  MaterialTypeEntity, 
  AuditLog,
  MaterialVersion
} from '../types/studyMaterial';

interface StudyMaterialContextProps {
  classes: ClassEntity[];
  subjects: SubjectEntity[];
  chapters: ChapterEntity[];
  materialTypes: MaterialTypeEntity[];
  materials: StudyMaterial[];
  auditLogs: AuditLog[];
  
  // Class Operations
  addClass: (name: string) => void;
  editClass: (id: string, name: string) => void;
  deleteClass: (id: string) => void;
  toggleClassActive: (id: string) => void;

  // Subject Operations
  addSubject: (name: string) => void;
  editSubject: (id: string, name: string) => void;
  deleteSubject: (id: string) => void;
  toggleSubjectActive: (id: string) => void;

  // Chapter Operations
  addChapter: (chapter: Omit<ChapterEntity, 'id' | 'createdAt'>) => void;
  editChapter: (id: string, chapter: Partial<Omit<ChapterEntity, 'id' | 'createdAt'>>) => void;
  deleteChapter: (id: string) => void;
  reorderChapters: (classId: string, subjectId: string, chapterIds: string[]) => void;

  // Material Type Operations
  addMaterialType: (name: string, description: string) => void;
  editMaterialType: (id: string, name: string, description: string) => void;
  deleteMaterialType: (id: string) => void;
  toggleMaterialTypeActive: (id: string) => void;

  // Study Material Operations
  uploadMaterial: (material: Omit<StudyMaterial, 'id' | 'uploadDate' | 'lastModified' | 'versions'>) => void;
  updateMaterial: (id: string, updates: Partial<Omit<StudyMaterial, 'id' | 'versions'>>) => void;
  replaceMaterialPDF: (id: string, fileName: string, fileSize: number, pages: number, readingTime: number, uploadedBy: string) => void;
  deleteMaterial: (id: string) => void;
  duplicateMaterial: (id: string) => void;
  archiveMaterial: (id: string) => void;
  rollbackVersion: (materialId: string, versionId: string) => void;

  // Bulk Operations
  bulkDelete: (ids: string[]) => void;
  bulkArchive: (ids: string[]) => void;
  bulkPublish: (ids: string[]) => void;
  bulkHide: (ids: string[]) => void;
  bulkMove: (ids: string[], targetClassId: string, targetSubjectId: string, targetChapterId: string) => void;

  // Logs
  addAuditLog: (action: string, details: string, performedBy: string) => void;
}

const StudyMaterialContext = createContext<StudyMaterialContextProps | undefined>(undefined);

export const StudyMaterialProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  localStorage.removeItem('mp_material_types');
  const initialMaterialTypes: MaterialTypeEntity[] = [
    { id: 'mt_notes', name: 'Study Notes', description: 'Chapter-wise premium notes with rich concepts', isActive: true, createdAt: '2026-01-01' },
        { id: 'mt_mindmap', name: 'Mind Map', description: 'Visual structure diagrams mapping major concepts', isActive: true, createdAt: '2026-01-01' },
    { id: 'mt_mcq', name: 'Practice MCQ\'s', description: 'Exam-ready multiple choice questions with detailed solutions', isActive: true, createdAt: '2026-01-01' },
    { id: 'mt_tf_fib', name: 'True or False / Fill Ups', description: 'Fact validation assertions and keyword memory retention sheets', isActive: true, createdAt: '2026-01-01' },
    { id: 'mt_ar', name: 'Assertion & Reason', description: 'Logical connectivity assertions with scientific correlation analysis', isActive: true, createdAt: '2026-01-01' },
    { id: 'mt_fc', name: 'Flash Cards', description: 'Interactive flashcards for core definitions and terms', isActive: true, createdAt: '2026-01-01' },
    { id: 'mt_qb', name: 'Question Bank', description: 'Extensive questions and answer banks', isActive: true, createdAt: '2026-01-01' },
    { id: 'mt_hots', name: 'HOTS Questions', description: 'High Order Thinking Skills questions for deep learning', isActive: true, createdAt: '2026-01-01' },
    { id: 'mt_case', name: 'Case Studies', description: 'Real exam case studies', isActive: true, createdAt: '2026-01-01' },
    { id: 'mt_qa', name: 'Question Answer (QA)', description: 'Direct question and answer revision blocks', isActive: true, createdAt: '2026-01-01' }
  ];

  const initialAuditLogs: AuditLog[] = [
    { id: 'log_1', action: 'Initialize Module', performedBy: 'System', details: 'Dynamic study material management database initialized.', timestamp: '2026-07-12 10:00' }
  ];

  // --- STATE INITIALIZATION ---
  const [classes, setClasses] = useState<ClassEntity[]>([]);
  const [subjects, setSubjects] = useState<SubjectEntity[]>([]);
  const [chapters, setChapters] = useState<ChapterEntity[]>([]);
  const [materials, setMaterials] = useState<StudyMaterial[]>([]);

      const [materialTypes, setMaterialTypes] = useState<MaterialTypeEntity[]>(() => {
    const stored = localStorage.getItem('mp_material_types');
    if (stored) {
      try {
        const parsed = JSON.parse(stored);
        if (Array.isArray(parsed) && parsed.length > 0) {
                    // Force check if mt_notes_text exists in parsed, if not add it
          return parsed.filter(p => p.id !== 'mt_notes_text').map(p => {
    if (p.id === 'mt_notes') p.name = 'Study Notes';
    return p;
  });
        }
      } catch (e) {}
    }
    return initialMaterialTypes;
  });

  const [auditLogs, setAuditLogs] = useState<AuditLog[]>(() => {
    const stored = localStorage.getItem('mp_audit_logs');
    if (stored) {
      try {
        const parsed = JSON.parse(stored);
        if (Array.isArray(parsed)) return parsed.filter(p => p.id !== 'mt_notes_text').map(p => {
    if (p.id === 'mt_notes') p.name = 'Study Notes';
    return p;
  });
      } catch (e) {}
    }
    return initialAuditLogs;
  });

  // --- FETCH REAL DATA FROM BACKEND APIS ON MOUNT ---
  useEffect(() => {
    const loadDataFromApi = async () => {
      try {
        const [clsRes, subRes, chRes, matRes] = await Promise.all([
          fetch('/api/admin/classes').catch(() => fetch('/api/master/classes')),
          fetch('/api/admin/subjects').catch(() => fetch('/api/master/subjects')),
          fetch('/api/admin/chapters').catch(() => fetch('/api/master/chapters')),
          fetch('/api/study-materials?summary=true').catch(() => fetch('/api/admin/study-materials?summary=true'))
        ]);

        if (clsRes && clsRes.ok) {
          const data = await clsRes.json();
          if (Array.isArray(data)) {
            setClasses(data.map((c: any) => ({
              id: String(c.id),
              name: c.class_name || c.name || `Class ${c.class_code}`,
              isActive: c.status === 'Active' || c.status === 'active' || true,
              createdAt: c.created_at || '2026-01-01'
            })));
          }
        }

        if (subRes && subRes.ok) {
          const data = await subRes.json();
          if (Array.isArray(data)) {
            const EXCLUDED_SUBJECTS = ['hindi', 'english', 'biology', 'chemistry', 'physics'];
            const processedSubjects: SubjectEntity[] = [];
            const seenNames = new Set<string>();

            data.forEach((s: any) => {
              let subName = String(s.subject_name || s.name || '').trim();
              if (subName.toLowerCase() === 'maths') {
                subName = 'Mathematics';
              }
              const lowerName = subName.toLowerCase();
              if (EXCLUDED_SUBJECTS.includes(lowerName)) return;

              if (!seenNames.has(lowerName)) {
                seenNames.add(lowerName);
                processedSubjects.push({
                  id: String(s.id),
                  name: subName,
                  isActive: (s.status === 'Active' || s.status === 'active' || true),
                  createdAt: s.created_at || '2026-01-01'
                });
              }
            });

            setSubjects(processedSubjects);
          }
        }

        if (chRes && chRes.ok) {
          const data = await chRes.json();
          if (Array.isArray(data)) {
            setChapters(data.map((ch: any) => ({
              id: String(ch.id),
              classId: String(ch.class_num || ch.classId || ''),
              subjectId: String(ch.subject || ch.subjectId || ''),
              chapterNumber: Number(ch.chapter_number || ch.chapterNumber || 1),
              name: ch.chapter_name || ch.name || '',
              description: ch.chapter_description || ch.description || '',
              thumbnail: ch.thumbnail || 'https://images.unsplash.com/photo-1530595467537-0b5996c41f2d?auto=format&fit=crop&q=80&w=200',
              isActive: true,
              createdAt: ch.created_at || '2026-01-01'
            })));
          }
        }

        if (matRes && matRes.ok) {
          const data = await matRes.json();
          const items = Array.isArray(data) ? data : (data.data || []);
          setMaterials(items.map((m: any) => ({
            id: String(m.id || m.material_id),
            material_id: m.material_id || String(m.id),
            source_id: m.source_id,
            material_type: m.material_type,
            content_format: m.content_format,
            image_path: m.image_path,
            classId: m.class_name || m.classId || '',
            subjectId: m.subject_name || m.subjectId || '',
            subject_name: m.subject_name,
            chapter_number: m.chapter_number,
            chapterId: String(m.chapter_number || m.chapterId || ''),
            materialTypeId: m.material_type_id || m.materialTypeId || m.material_type || '',
            academicSession: m.academicSession || '2026-27',
            title: m.title || '',
            description: m.description || '',
            fileName: m.file_name || m.fileName || '',
            thumbnail: m.thumbnail || m.thumbnail_path || 'https://images.unsplash.com/photo-1434030216411-0b793f4b4173?auto=format&fit=crop&q=80&w=200',
            fileSize: typeof m.file_size === 'number' ? m.file_size : parseFloat(String(m.file_size || '0')),
            pages: Number(m.pages || m.total_pages || 1),
            readingTime: Number(m.readingTime || 10),
            version: m.version || 'v1',
            uploadDate: m.created_at || m.uploadDate || '2026-08-05',
            uploadedBy: m.uploaded_by || m.uploadedBy || 'admin@mcqpulse.com',
            lastModified: m.updated_at || m.lastModified || '2026-08-05',
            status: (m.status as any) || 'Published',
            visibility: (m.visibility as any) || 'Everyone',
            versions: m.versions || [],
            pdfUrl: m.pdf_url || m.pdfUrl,
            html_content: m.html_content,
            fileUrl: m.file_url || m.fileUrl
          })));
        }
      } catch (err) {
        console.error('Failed to load study material context data from API:', err);
      }
    };

    loadDataFromApi();
  }, []);

  useEffect(() => {
    localStorage.setItem('mp_audit_logs', JSON.stringify(auditLogs));
  }, [auditLogs]);

  useEffect(() => {
    localStorage.setItem('mp_material_types', JSON.stringify(materialTypes));
  }, [materialTypes]);



  // --- OPERATIONS HELPERS ---

  const addAuditLog = (action: string, details: string, performedBy: string) => {
    const newLog: AuditLog = {
      id: `log_${Date.now()}`,
      action,
      details,
      performedBy,
      timestamp: new Date().toISOString().replace('T', ' ').substring(0, 16)
    };
    setAuditLogs(prev => [newLog, ...prev]);
  };

  // --- CLASS ACTIONS ---
  const addClass = (name: string) => {
    const id = `c_${name.toLowerCase().replace(/\s+/g, '_')}_${Date.now()}`;
    const newClass: ClassEntity = {
      id,
      name,
      isActive: true,
      createdAt: new Date().toLocaleDateString()
    };
    setClasses(prev => [...prev, newClass]);
    addAuditLog('Create Class', `Added new class category "${name}"`, 'admin@mcqpulse.com');
  };

  const editClass = (id: string, name: string) => {
    setClasses(prev => prev.map(c => c.id === id ? { ...c, name } : c));
    addAuditLog('Edit Class', `Modified class identifier to "${name}"`, 'admin@mcqpulse.com');
  };

  const deleteClass = (id: string) => {
    const cName = classes.find(c => c.id === id)?.name || id;
    setClasses(prev => prev.filter(c => c.id !== id));
    addAuditLog('Delete Class', `Removed class category "${cName}"`, 'admin@mcqpulse.com');
  };

  const toggleClassActive = (id: string) => {
    setClasses(prev => prev.map(c => {
      if (c.id === id) {
        const nextState = !c.isActive;
        addAuditLog('Toggle Class Status', `${nextState ? 'Activated' : 'Deactivated'} class ${c.name}`, 'admin@mcqpulse.com');
        return { ...c, isActive: nextState };
      }
      return c;
    }));
  };

  // --- SUBJECT ACTIONS ---
  const addSubject = (name: string) => {
    const id = `s_${name.toLowerCase().replace(/\s+/g, '_')}_${Date.now()}`;
    const newSubject: SubjectEntity = {
      id,
      name,
      isActive: true,
      createdAt: new Date().toLocaleDateString()
    };
    setSubjects(prev => [...prev, newSubject]);
    addAuditLog('Create Subject', `Added new subject template "${name}"`, 'admin@mcqpulse.com');
  };

  const editSubject = (id: string, name: string) => {
    setSubjects(prev => prev.map(s => s.id === id ? { ...s, name } : s));
    addAuditLog('Edit Subject', `Updated subject header to "${name}"`, 'admin@mcqpulse.com');
  };

  const deleteSubject = (id: string) => {
    const sName = subjects.find(s => s.id === id)?.name || id;
    setSubjects(prev => prev.filter(s => s.id !== id));
    addAuditLog('Delete Subject', `Removed subject template "${sName}"`, 'admin@mcqpulse.com');
  };

  const toggleSubjectActive = (id: string) => {
    setSubjects(prev => prev.map(s => {
      if (s.id === id) {
        const nextState = !s.isActive;
        addAuditLog('Toggle Subject Status', `${nextState ? 'Enabled' : 'Disabled'} subject ${s.name}`, 'admin@mcqpulse.com');
        return { ...s, isActive: nextState };
      }
      return s;
    }));
  };

  // --- CHAPTER ACTIONS ---
  
  
  
  
  const getAuthToken = () => localStorage.getItem('mcq_pulse_token');

  const addChapter = async (chapter: Omit<ChapterEntity, 'id' | 'createdAt'>) => {
    try {
      const class_num = parseInt(chapter.classId.replace('c_', ''), 10);
      const subject = chapter.subjectId === 's_sci' ? 'Science' : chapter.subjectId === 's_math' ? 'Mathematics' : 'Social Science';
      const res = await fetch('/api/admin/chapters', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${getAuthToken()}` },
        body: JSON.stringify({ class_num, subject, chapter_number: chapter.chapterNumber, chapter_name: chapter.name })
      });
      if (res.ok) {
        window.location.reload();
      }
    } catch(err) {}
  };

  const editChapter = async (id: string, updates: Partial<Omit<ChapterEntity, 'id' | 'createdAt'>>) => {
    try {
      const ch = chapters.find(c => c.id === id);
      if(!ch) return;
      const res = await fetch(`/api/admin/chapters/${id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${getAuthToken()}` },
        body: JSON.stringify({ chapter_number: updates.chapterNumber || ch.chapterNumber, chapter_name: updates.name || ch.name })
      });
      if (res.ok) {
        window.location.reload();
      }
    } catch(err) {}
  };

  const deleteChapter = async (id: string) => {
    try {
      const res = await fetch(`/api/admin/chapters/${id}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${getAuthToken()}` }
      });
      if (res.ok) {
        window.location.reload();
      }
    } catch(err) {}
  };

  const reorderChapters = (classId: string, subjectId: string, chapterIds: string[]) => {
    setChapters(prev => {
      const rest = prev.filter(ch => !(ch.classId === classId && ch.subjectId === subjectId));
      const targetList = prev.filter(ch => ch.classId === classId && ch.subjectId === subjectId);
      
      const ordered = chapterIds.map((id, index) => {
        const ch = targetList.find(c => c.id === id);
        return ch ? { ...ch, chapterNumber: index + 1 } : null;
      }).filter(Boolean) as ChapterEntity[];

      return [...rest, ...ordered];
    });
    addAuditLog('Reorder Chapters', `Reordered chapters sequence index for Class ID: ${classId}, Subject ID: ${subjectId}`, 'admin@mcqpulse.com');
  };

  // --- MATERIAL TYPE ACTIONS ---
  const addMaterialType = (name: string, description: string) => {
    const id = `mt_${name.toLowerCase().replace(/\s+/g, '_')}_${Date.now()}`;
    const newType: MaterialTypeEntity = {
      id,
      name,
      description,
      isActive: true,
      createdAt: new Date().toLocaleDateString()
    };
    setMaterialTypes(prev => [...prev, newType]);
    addAuditLog('Create Material Type', `Added custom academic format: "${name}"`, 'admin@mcqpulse.com');
  };

  const editMaterialType = (id: string, name: string, description: string) => {
    setMaterialTypes(prev => prev.map(t => t.id === id ? { ...t, name, description } : t));
    addAuditLog('Edit Material Type', `Modified description of "${name}" format`, 'admin@mcqpulse.com');
  };

  const deleteMaterialType = (id: string) => {
    const tName = materialTypes.find(t => t.id === id)?.name || id;
    setMaterialTypes(prev => prev.filter(t => t.id !== id));
    addAuditLog('Delete Material Type', `De-registered format category "${tName}"`, 'admin@mcqpulse.com');
  };

  const toggleMaterialTypeActive = (id: string) => {
    setMaterialTypes(prev => prev.map(t => {
      if (t.id === id) {
        const nextState = !t.isActive;
        addAuditLog('Toggle Material Type Status', `${nextState ? 'Activated' : 'Suspended'} format ${t.name}`, 'admin@mcqpulse.com');
        return { ...t, isActive: nextState };
      }
      return t;
    }));
  };

  // --- STUDY MATERIAL ACTIONS ---
  const uploadMaterial = (material: Omit<StudyMaterial, 'id' | 'uploadDate' | 'lastModified' | 'versions'>) => {
    const id = `mat_${Date.now()}`;
    const timestamp = new Date().toISOString().replace('T', ' ').substring(0, 16);
    
    const versionObj: MaterialVersion = {
      id: `ver_${Date.now()}_1`,
      version: material.version || 'v1',
      fileName: material.fileName,
      fileSize: material.fileSize,
      pages: material.pages,
      uploadDate: timestamp,
      uploadedBy: material.uploadedBy
    };

    const newMaterial: StudyMaterial = {
      ...material,
      id,
      uploadDate: timestamp,
      lastModified: timestamp,
      versions: [versionObj]
    };

    setMaterials(prev => [newMaterial, ...prev]);
    addAuditLog('Upload Material', `Successfully compiled and dispatched material "${material.title}" (${material.version})`, material.uploadedBy);
  };

  const updateMaterial = (id: string, updates: Partial<Omit<StudyMaterial, 'id' | 'versions'>>) => {
    const timestamp = new Date().toISOString().replace('T', ' ').substring(0, 16);
    setMaterials(prev => prev.map(mat => {
      if (mat.id === id) {
        addAuditLog('Update Material Details', `Edited meta records for: "${mat.title}"`, 'admin@mcqpulse.com');
        return {
          ...mat,
          ...updates,
          lastModified: timestamp
        };
      }
      return mat;
    }));
  };

  const replaceMaterialPDF = (
    id: string, 
    fileName: string, 
    fileSize: number, 
    pages: number, 
    readingTime: number, 
    uploadedBy: string
  ) => {
    const timestamp = new Date().toISOString().replace('T', ' ').substring(0, 16);
    setMaterials(prev => prev.map(mat => {
      if (mat.id === id) {
        // Calculate next version
        const currentVerNum = parseInt(mat.version.replace('v', '')) || 1;
        const nextVer = `v${currentVerNum + 1}`;
        
        const newVerObj: MaterialVersion = {
          id: `ver_${Date.now()}`,
          version: nextVer,
          fileName,
          fileSize,
          pages,
          uploadDate: timestamp,
          uploadedBy
        };

        addAuditLog('Replace PDF Asset', `Replaced document on "${mat.title}" - Roll forward to ${nextVer}`, uploadedBy);

        return {
          ...mat,
          fileName,
          fileSize,
          pages,
          readingTime,
          version: nextVer,
          lastModified: timestamp,
          versions: [newVerObj, ...mat.versions]
        };
      }
      return mat;
    }));
  };

  const deleteMaterial = (id: string) => {
    const mTitle = materials.find(m => m.id === id)?.title || id;
    setMaterials(prev => prev.filter(m => m.id !== id));
    addAuditLog('Delete Material', `Purged document catalog node: "${mTitle}"`, 'admin@mcqpulse.com');
  };

  const duplicateMaterial = (id: string) => {
    const target = materials.find(m => m.id === id);
    if (!target) return;

    const newId = `mat_${Date.now()}`;
    const timestamp = new Date().toISOString().replace('T', ' ').substring(0, 16);
    
    const duplicated: StudyMaterial = {
      ...target,
      id: newId,
      title: `${target.title} (Copy)`,
      uploadDate: timestamp,
      lastModified: timestamp,
      versions: target.versions.map(v => ({
        ...v,
        id: `ver_${Date.now()}_${Math.random().toString(36).substr(2, 4)}`
      }))
    };

    setMaterials(prev => [duplicated, ...prev]);
    addAuditLog('Duplicate Material', `Created duplicate instance: "${target.title}"`, 'admin@mcqpulse.com');
  };

  const archiveMaterial = (id: string) => {
    setMaterials(prev => prev.map(m => {
      if (m.id === id) {
        addAuditLog('Archive Material', `Moved "${m.title}" to archives`, 'admin@mcqpulse.com');
        return { ...m, status: 'Archived' };
      }
      return m;
    }));
  };

  const rollbackVersion = (materialId: string, versionId: string) => {
    const timestamp = new Date().toISOString().replace('T', ' ').substring(0, 16);
    setMaterials(prev => prev.map(mat => {
      if (mat.id === materialId) {
        const targetVer = mat.versions.find(v => v.id === versionId);
        if (!targetVer) return mat;

        addAuditLog('Version Rollback', `Rolled back "${mat.title}" to version ${targetVer.version} (${targetVer.fileName})`, 'admin@mcqpulse.com');

        return {
          ...mat,
          version: targetVer.version,
          fileName: targetVer.fileName,
          fileSize: targetVer.fileSize,
          pages: targetVer.pages,
          lastModified: timestamp
        };
      }
      return mat;
    }));
  };

  // --- BULK OPERATIONS ---
  const bulkDelete = (ids: string[]) => {
    setMaterials(prev => prev.filter(m => !ids.includes(m.id)));
    addAuditLog('Bulk Operations', `Purged ${ids.length} documents from registry`, 'admin@mcqpulse.com');
  };

  const bulkArchive = (ids: string[]) => {
    setMaterials(prev => prev.map(m => ids.includes(m.id) ? { ...m, status: 'Archived' } : m));
    addAuditLog('Bulk Operations', `Archived ${ids.length} files in batch dispatch`, 'admin@mcqpulse.com');
  };

  const bulkPublish = (ids: string[]) => {
    setMaterials(prev => prev.map(m => ids.includes(m.id) ? { ...m, status: 'Published' } : m));
    addAuditLog('Bulk Operations', `Published ${ids.length} files in batch dispatch`, 'admin@mcqpulse.com');
  };

  const bulkHide = (ids: string[]) => {
    setMaterials(prev => prev.map(m => ids.includes(m.id) ? { ...m, status: 'Draft' } : m));
    addAuditLog('Bulk Operations', `Hid ${ids.length} files (reverted to Draft) in batch`, 'admin@mcqpulse.com');
  };

  const bulkMove = (ids: string[], targetClassId: string, targetSubjectId: string, targetChapterId: string) => {
    const cName = classes.find(c => c.id === targetClassId)?.name || 'Class';
    const sName = subjects.find(s => s.id === targetSubjectId)?.name || 'Subject';
    const chName = chapters.find(ch => ch.id === targetChapterId)?.name || 'Chapter';

    setMaterials(prev => prev.map(m => ids.includes(m.id) ? { 
      ...m, 
      classId: targetClassId, 
      subjectId: targetSubjectId, 
      chapterId: targetChapterId 
    } : m));

    addAuditLog('Bulk Operations', `Bulk relocated ${ids.length} files to ${cName} → ${sName} → ${chName}`, 'admin@mcqpulse.com');
  };

  return (
    <StudyMaterialContext.Provider value={{
      classes,
      subjects,
      chapters,
      materialTypes,
      materials,
      auditLogs,
      
      addClass,
      editClass,
      deleteClass,
      toggleClassActive,

      addSubject,
      editSubject,
      deleteSubject,
      toggleSubjectActive,

      addChapter,
      editChapter,
      deleteChapter,
      reorderChapters,

      addMaterialType,
      editMaterialType,
      deleteMaterialType,
      toggleMaterialTypeActive,

      uploadMaterial,
      updateMaterial,
      replaceMaterialPDF,
      deleteMaterial,
      duplicateMaterial,
      archiveMaterial,
      rollbackVersion,

      bulkDelete,
      bulkArchive,
      bulkPublish,
      bulkHide,
      bulkMove,

      addAuditLog
    }}>
      {children}
    </StudyMaterialContext.Provider>
  );
};

export const useStudyMaterial = () => {
  const context = useContext(StudyMaterialContext);
  if (!context) {
    throw new Error('useStudyMaterial must be used within a StudyMaterialProvider');
  }
  return context;
};
